import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Sparkle, Warning, ArrowUpRight, ClockCounterClockwise } from "@phosphor-icons/react";
import MarkdownDocument from "@/components/MarkdownDocument";
import { ENGAGEMENT_STAGE_LABEL, ENGAGEMENT_STATUS_LABEL, ENGAGEMENT_HEALTH_LABEL } from "@/lib/enumLabels";

/**
 * ClientHealthStoryline — advisory narrative + sourced evidence.
 *
 * The endpoint returns BOTH an AI narrative and the raw evidence it was
 * grounded in. We render them side-by-side: a prominent "Provisional AI
 * interpretation" banner over the narrative, and a fully sourced evidence
 * column that partners can consult directly. The narrative is optional —
 * if Claude fails or the record is thin, the evidence view still stands on
 * its own.
 */
export default function ClientHealthStoryline({ clientId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/clients/${clientId}/health-storyline`);
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load storyline");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (clientId) load(); /* eslint-disable-next-line */ }, [clientId]);

  const regen = async () => {
    setRegenerating(true);
    try {
      const { data } = await api.get(`/clients/${clientId}/health-storyline`);
      setData(data);
      toast.success("Storyline regenerated");
    } catch (e) {
      toast.error("Regenerate failed");
    } finally { setRegenerating(false); }
  };

  if (loading) return <div className="border border-border bg-card p-8 text-xs text-muted-foreground" data-testid="storyline-loading">Assembling the arc…</div>;
  if (!data) return null;

  const ev = data.evidence || {};
  const hasNarrative = !!(data.narrative && data.narrative.trim());

  return (
    <div className="space-y-6" data-testid="client-health-storyline">
      {/*
        Provisional banner is intentionally prominent. Every claim in the
        narrative is an interpretation — the evidence column below is the
        source of truth. Partners should never confuse this with a decision.
      */}
      <div className="border-l-2 border-amber-500/70 bg-amber-500/5 px-4 py-3 flex items-start gap-3" data-testid="storyline-provisional-banner">
        <Warning size={16} weight="fill" className="text-amber-500 shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-amber-500">Provisional AI interpretation</div>
          <div className="text-xs text-muted-foreground mt-1">
            Read this as an advisor's provisional read, grounded in the evidence below — not a final judgment.
            Every claim maps to a specific signal you can verify in the source column.
          </div>
        </div>
        <Button
          onClick={regen}
          disabled={regenerating}
          size="sm"
          variant="outline"
          className="rounded-none border-primary/60 text-primary hover:bg-primary/10 shrink-0"
          data-testid="storyline-regen"
        >
          <Sparkle size={12} weight={regenerating ? "regular" : "fill"} className="mr-1.5" />
          {regenerating ? "Regenerating…" : "Regenerate"}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-6">
        <div className="border border-border bg-card p-6" data-testid="storyline-narrative">
          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-primary flex items-center gap-1.5 mb-3">
            <Sparkle size={11} weight="fill" /> Advisor storyline · draft
          </div>
          {hasNarrative ? (
            <MarkdownDocument>{data.narrative}</MarkdownDocument>
          ) : (
            <div className="text-sm text-muted-foreground italic">
              Not enough signal yet to draft a storyline. Add a meeting note or update the relationship health, then regenerate.
            </div>
          )}
          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mt-4 pt-3 border-t border-border">
            Generated {new Date(data.generated_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
          </div>
        </div>

        {/*
          Evidence column — one card per signal type. Every item is a link
          to its source record so the partner can verify the interpretation
          without leaving the page.
        */}
        <div className="space-y-4" data-testid="storyline-evidence">
          <EvidenceCard
            title="Health transitions"
            testid="evidence-health"
            empty="No health-status transitions recorded yet."
            items={ev.health_transitions}
            render={(t, i) => (
              <div key={i} className="text-xs">
                <span className="font-mono uppercase tracking-widest text-muted-foreground">{(t.date || "").slice(0, 10)}</span>
                <span className="ml-2 text-foreground">{t.from} → <span className="font-medium">{t.to}</span></span>
                {t.changed_by && <span className="ml-1 text-muted-foreground">· by {t.changed_by}</span>}
              </div>
            )}
          />
          <EvidenceCard
            title="Recent notes"
            testid="evidence-notes"
            empty="No notes captured yet."
            items={ev.recent_notes}
            render={(n) => {
              const to = n.engagement_id
                ? `/engagements/${n.engagement_id}?tab=notes`
                : `/clients/${clientId}?tab=notes`;
              return (
                <Link key={n.id} to={to} className="block text-xs hover:text-primary" data-testid={`evidence-note-${n.id}`}>
                  <div className="font-medium truncate">{n.title}</div>
                  <div className="font-mono uppercase tracking-widest text-muted-foreground text-[0.625rem]">
                    {n.note_type} · {(n.meeting_date || n.created_at || "").slice(0, 10)}
                    <ArrowUpRight size={10} weight="bold" className="inline ml-1" />
                  </div>
                </Link>
              );
            }}
          />
          <EvidenceCard
            title="Engagements"
            testid="evidence-engagements"
            empty="No engagements yet."
            items={ev.engagements}
            render={(e) => (
              <Link key={e.id} to={`/engagements/${e.id}`} className="block text-xs hover:text-primary" data-testid={`evidence-engagement-${e.id}`}>
                <div className="font-medium truncate">{e.name}</div>
                <div className="font-mono uppercase tracking-widest text-muted-foreground text-[0.625rem]">
                  {ENGAGEMENT_STAGE_LABEL[e.stage] || e.stage} · {ENGAGEMENT_STATUS_LABEL[e.status] || e.status} · health {ENGAGEMENT_HEALTH_LABEL[e.health] || e.health} <ArrowUpRight size={10} weight="bold" className="inline ml-1" />
                </div>
              </Link>
            )}
          />
          <EvidenceCard
            title="Overdue tasks"
            testid="evidence-tasks"
            empty="Nothing overdue right now."
            items={ev.overdue_tasks}
            render={(t) => (
              <Link
                key={t.id}
                to={t.engagement_id ? `/engagements/${t.engagement_id}?tab=tasks` : "/tasks"}
                className="block text-xs hover:text-primary"
                data-testid={`evidence-task-${t.id}`}
              >
                <div className="font-medium truncate">{t.title}</div>
                <div className="font-mono uppercase tracking-widest text-muted-foreground text-[0.625rem]">
                  <ClockCounterClockwise size={10} weight="bold" className="inline mr-1" /> overdue since {t.due_date}
                </div>
              </Link>
            )}
          />
        </div>
      </div>
    </div>
  );
}

function EvidenceCard({ title, items, empty, render, testid }) {
  return (
    <div className="border border-border bg-card p-4" data-testid={testid}>
      <div className="text-[0.625rem] font-mono uppercase tracking-widest text-primary mb-2">{title} · {items?.length || 0}</div>
      {(!items || items.length === 0) ? (
        <div className="text-xs text-muted-foreground italic">{empty}</div>
      ) : (
        <div className="space-y-2">{items.map(render)}</div>
      )}
    </div>
  );
}
